import axios from 'axios';

import { LOCAL_STORAGE_KEY_ACCESS_TOKEN } from 'src/services/constants/LocalStorage';
import { ModulesApiRoutes } from '../drivers';

const extractEnvelopeMessage = (payload) => {
    const message = payload?.message;

    if (typeof message === 'string' && message) {
        return message;
    }

    if (message?.description) {
        return message.description;
    }

    if (payload?.error && typeof payload.error === 'string') {
        return payload.error;
    }

    return 'Request failed';
};

const unwrapEnvelope = (response) => {
    const payload = response?.data;

    if (payload && payload.ok === false) {
        const error = new Error(extractEnvelopeMessage(payload));
        error.response = { data: payload };
        throw error;
    }

    return payload;
};

export default class MailboxService {
    static instances = {};

    constructor(baseUrl) {
        this.baseUrl = baseUrl;
        this.resource = `${baseUrl}/mailbox`;
    }

    static getInstance(baseUrl) {
        const resolved = baseUrl || ModulesApiRoutes.gd.API_BASE_URL;

        if (!MailboxService.instances[resolved]) {
            MailboxService.instances[resolved] = new MailboxService(resolved);
        }

        return MailboxService.instances[resolved];
    }

    getAuthHeaders() {
        return {
            Authorization: `Bearer ${localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN)}`,
        };
    }

    getJsonHeaders() {
        return {
            ...this.getAuthHeaders(),
            'Content-Type': 'application/json',
        };
    }

    async fetchMessages(params = {}) {
        const response = await axios.get(`${this.resource}/messages`, {
            headers: this.getAuthHeaders(),
            params,
        });

        return unwrapEnvelope(response);
    }

    async fetchUnreadCount(params = {}) {
        const response = await axios.get(`${this.resource}/unread-count`, {
            headers: this.getAuthHeaders(),
            params,
        });

        return unwrapEnvelope(response);
    }

    async fetchMessage(id) {
        const response = await axios.get(
            `${this.resource}/messages/${encodeURIComponent(id)}`,
            {
                headers: this.getAuthHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async sendMessage(payload) {
        const response = await axios.post(
            `${this.resource}/messages`,
            payload,
            {
                headers: this.getJsonHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async updateMessageFlags(id, payload) {
        const response = await axios.post(
            `${this.resource}/messages/${encodeURIComponent(id)}/flags`,
            payload,
            {
                headers: this.getJsonHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async updateMessagesFlags(payload) {
        const response = await axios.post(
            `${this.resource}/messages/flags`,
            payload,
            {
                headers: this.getJsonHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async trashMessage(id) {
        const response = await axios.post(
            `${this.resource}/messages/${encodeURIComponent(id)}/trash`,
            {},
            {
                headers: this.getJsonHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async restoreMessage(id) {
        const response = await axios.post(
            `${this.resource}/messages/${encodeURIComponent(id)}/restore`,
            {},
            {
                headers: this.getJsonHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async syncMailbox() {
        const response = await axios.post(
            `${this.resource}/sync`,
            {},
            {
                headers: this.getJsonHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async fetchIntents() {
        const response = await axios.get(`${this.resource}/intents`, {
            headers: this.getAuthHeaders(),
        });

        return unwrapEnvelope(response);
    }

    async analyzeMessage(id, payload) {
        const response = await axios.post(
            `${this.resource}/messages/${encodeURIComponent(id)}/analyze`,
            payload,
            {
                headers: this.getJsonHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async applyMessageIntent(id, payload) {
        const response = await axios.post(
            `${this.resource}/messages/${encodeURIComponent(id)}/intents`,
            payload,
            {
                headers: this.getJsonHeaders(),
            }
        );

        return unwrapEnvelope(response);
    }

    async fetchAttachment(id) {
        const response = await axios.get(
            `${this.resource}/attachments/${encodeURIComponent(id)}`,
            {
                headers: this.getAuthHeaders(),
                responseType: 'blob',
            }
        );

        const blob = response.data;
        const contentType = response.headers['content-type'] || blob?.type || '';
        const contentDisposition = response.headers['content-disposition'] || '';

        if (contentType.includes('application/json') && blob && typeof blob.text === 'function') {
            const text = await blob.text();
            let payload = null;

            try {
                payload = JSON.parse(text);
            } catch (error) {
                return {
                    blob: new Blob([text], { type: contentType }),
                    contentType,
                    contentDisposition,
                };
            }

            if (payload && payload.ok === false) {
                const error = new Error(extractEnvelopeMessage(payload));
                error.response = { data: payload };
                throw error;
            }
        }

        return {
            blob,
            contentType,
            contentDisposition,
        };
    }
}
